import { css } from '@emotion/react'
import styled from '@emotion/styled'
import dayjs from 'dayjs'
import React, { useState } from 'react'
import { PotentialDuplicate } from '../../../hooks/useDuplicateCheck'
import { borderRadius } from '../../../styles/vars'
import { eventTimeZone } from '../../../util/dateTime'

const WarningContainer = styled.div`
  background-color: #fff8e6;
  border: 1px solid #f5c518;
  border-radius: ${borderRadius};
  padding: 12px 16px;
  margin: 12px 0;
`

const WarningHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
`

const WarningIcon = styled.span`
  margin-right: 8px;
  font-size: 16px;
`

const WarningText = styled.span`
  flex: 1;
  color: #856404;
  font-weight: 500;
  font-size: 14px;
`

const ViewMatchesButton = styled.button`
  background: none;
  border: none;
  color: #0066cc;
  font-size: 13px;
  cursor: pointer;
  padding: 4px 8px;
  text-decoration: underline;

  &:hover {
    color: #004499;
  }
`

const MatchesList = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e6d9b8;
`

const EventCard = styled.div`
  background: white;
  border: 1px solid #ddd;
  border-radius: ${borderRadius};
  padding: 12px;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`

const EventTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 4px;
  color: #333;
`

const EventDetails = styled.div`
  font-size: 13px;
  color: #666;
  margin-bottom: 8px;
`

const EventReasons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 8px;
`

const ReasonTag = styled.span`
  background: #e8f4fd;
  color: #0066cc;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
`

const ViewEventLink = styled.a`
  color: #0066cc;
  font-size: 13px;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

const LoadingIndicator = styled.span`
  font-size: 13px;
  color: #666;
  font-style: italic;
`

type Props = {
  potentialDuplicates: PotentialDuplicate[]
  isChecking: boolean
}

const DuplicateWarning: React.FC<Props> = ({ potentialDuplicates, isChecking }) => {
  const [isExpanded, setIsExpanded] = useState(false)

  if (isChecking) {
    return (
      <WarningContainer
        css={css`
          background-color: #f8f9fa;
          border-color: #dee2e6;
        `}
      >
        <LoadingIndicator>Checking for similar events...</LoadingIndicator>
      </WarningContainer>
    )
  }

  if (potentialDuplicates.length === 0) {
    return null
  }

  // These are saved events, so they render at their own venue's clock rather
  // than the submitter's — otherwise a cross-zone submitter compares the time
  // they just typed against a duplicate shown in a different zone.
  const formatDate = (dateStr: string | null, timeZone: string) => {
    if (!dateStr) return 'Date not set'
    return dayjs(dateStr).tz(timeZone).format('MMM D, YYYY [at] h:mm A')
  }

  return (
    <WarningContainer>
      <WarningHeader onClick={() => setIsExpanded(!isExpanded)}>
        <WarningIcon>&#9888;</WarningIcon>
        <WarningText>
          {potentialDuplicates.length} similar event{potentialDuplicates.length > 1 ? 's' : ''} found for this date
        </WarningText>
        <ViewMatchesButton
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            setIsExpanded(!isExpanded)
          }}
        >
          {isExpanded ? 'Hide matches' : 'View matches'}
        </ViewMatchesButton>
      </WarningHeader>

      {isExpanded && (
        <MatchesList>
          {potentialDuplicates.map((duplicate) => (
            <EventCard key={duplicate.id}>
              <EventTitle>{duplicate.title}</EventTitle>
              <EventDetails>
                {formatDate(duplicate.startAt, eventTimeZone(duplicate))}
                {duplicate.placeTitle && ` • ${duplicate.placeTitle}`}
              </EventDetails>
              <EventReasons>
                {duplicate.similarityReasons.map((reason, index) => (
                  <ReasonTag key={index}>{reason}</ReasonTag>
                ))}
              </EventReasons>
              <ViewEventLink href={duplicate.eventUrl} target="_blank" rel="noopener noreferrer">
                View Event &rarr;
              </ViewEventLink>
            </EventCard>
          ))}
          <div
            css={css`
              margin-top: 12px;
              font-size: 12px;
              color: #666;
              font-style: italic;
            `}
          >
            If this is a different event, you can continue with your submission.
          </div>
        </MatchesList>
      )}
    </WarningContainer>
  )
}

export default DuplicateWarning
